import { useEffect, useRef, useCallback } from 'preact/hooks';
import type { Design } from '../../types';
import { renderPreview } from '../../engine/renderer';

interface Props {
  design: Design;
  className?: string;
}

function useDebounce<T extends (...args: unknown[]) => void>(fn: T, delay: number): T {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  return useCallback(
    ((...args: unknown[]) => {
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => fn(...args), delay);
    }) as T,
    [fn, delay],
  );
}

export function CanvasPreview({ design, className }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const doRender = useCallback(() => {
    if (canvasRef.current) {
      renderPreview(canvasRef.current, design);
    }
  }, [design]);

  const debouncedRender = useDebounce(doRender, 150);

  useEffect(() => {
    // Immediate render on first mount or style changes; debounce on text
    doRender();
  }, [design.style.preset, design.style.textColor, design.style.backgroundColor,
      design.style.secondaryColor, design.style.textOrientation, design.style.fontFamily,
      design.uchiwaType]);

  useEffect(() => {
    debouncedRender();
  }, [design.text]);

  return (
    <canvas
      ref={canvasRef}
      class={className}
      style={{
        maxWidth: '100%',
        maxHeight: '100%',
        width: 'auto',
        height: 'auto',
        borderRadius: '20px',
        boxShadow: '0 10px 30px rgba(0,0,0,0.12)',
      }}
    />
  );
}
